import { DataService } from '../services/data.js';
import { formatMoney, getIcon, showToast } from '../components/ui.js';

export async function renderRatesView() {
  const rates = await DataService.getRates();

  return `
    <div class="space-y-6">
      <!-- Title & Action -->
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-3 border-b border-border">
        <div>
          <h1 class="text-2xl font-bold tracking-tight text-text-primary">ABM de Tarifas y Costos Horarios</h1>
          <p class="text-xs text-text-secondary mt-0.5">Configuración de valor hora y traslados de comisiones según canal de cobro</p>
        </div>
        <button id="btn-open-rate-modal" class="px-3.5 py-2 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary-hover active:bg-primary-active transition-all shadow-sm flex items-center gap-2">
          ${getIcon('plus', 'w-3.5 h-3.5')}
          <span>+ Nueva Tarifa</span>
        </button>
      </div>

      <!-- Explanatory Banner -->
      <div class="bg-surface p-4 rounded-md border border-border shadow-card flex items-start gap-3">
        <div class="p-2 rounded bg-surface-subtle text-text-primary shrink-0 mt-0.5">
          ${getIcon('tag', 'w-4 h-4')}
        </div>
        <div class="text-xs text-text-secondary space-y-1">
          <p class="font-bold text-text-primary">Lógica de Traslado de Comisiones e Impuestos</p>
          <p class="leading-relaxed">
            Cada tarifa define el valor por hora pactado con el cliente. En canales como <b>Fiverr</b> o cobros del <b>Exterior Directo</b> (Payoneer / Takenos), la tarifa se ajusta automáticamente incorporando el porcentaje de retención para asegurar que el neto final percibido por hora de trabajo no sufra depreciación.
          </p>
        </div>
      </div>

      <!-- Rates Grid / Cards -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
        ${rates.map(rate => `
          <div class="bg-surface p-5 rounded-md border border-border shadow-card flex flex-col justify-between space-y-4">
            <div>
              <div class="flex items-center justify-between pb-2 border-b border-border">
                <h3 class="font-bold text-base text-text-primary">${rate.name}</h3>
                <span class="text-[10px] font-mono font-semibold px-2 py-0.5 rounded ${
                  rate.is_active ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-surface-subtle text-text-secondary'
                }">
                  ${rate.is_active ? 'Activa' : 'Inactiva'}
                </span>
              </div>

              <div class="mt-4 space-y-2.5">
                <div>
                  <span class="text-[10px] font-semibold text-text-secondary uppercase tracking-wider block">Valor Hora</span>
                  <span class="text-2xl font-bold font-mono text-text-primary">
                    ${formatMoney(rate.hourly_rate, rate.currency)}
                  </span>
                  <span class="text-xs text-text-secondary font-mono">/ hora</span>
                </div>

                <div class="pt-2 border-t border-border/60">
                  <span class="text-[10px] text-text-secondary font-semibold uppercase block">Comisiones Trasladadas</span>
                  <span class="text-sm font-mono font-bold text-text-primary">
                    ${rate.commission_percent || 0}%
                  </span>
                  <span class="text-[11px] text-text-secondary block mt-0.5">
                    ${rate.commission_percent > 0 ? 'Retención agregada al precio' : 'Sin comisiones intermedias'}
                  </span>
                </div>

                ${rate.notes ? `
                  <p class="text-xs text-text-secondary italic pt-2 border-t border-border/60">
                    "${rate.notes}"
                  </p>
                ` : ''}
              </div>
            </div>

            <div class="pt-3 border-t border-border flex justify-end">
              <button 
                class="btn-edit-rate px-3 py-1.5 text-xs font-semibold rounded border border-border bg-surface-subtle hover:bg-border/30 text-text-primary transition-colors flex items-center gap-1.5"
                data-rate-id="${rate.id}"
                data-rate-name="${rate.name}"
                data-rate-amount="${rate.hourly_rate}"
                data-rate-currency="${rate.currency}"
                data-rate-commission="${rate.commission_percent}"
                data-rate-notes="${rate.notes || ''}"
              >
                ${getIcon('edit', 'w-3 h-3')}
                <span>Editar Tarifa</span>
              </button>
            </div>
          </div>
        `).join('')}
      </div>
    </div>

    <!-- Modal Editar / Crear Tarifa -->
    <div id="modal-rate" class="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 hidden flex items-center justify-center p-4">
      <div class="bg-surface rounded-md border border-border shadow-xl max-w-md w-full p-6 space-y-4 animate-fade-in">
        <div class="flex items-center justify-between pb-3 border-b border-border">
          <h3 id="modal-rate-title" class="font-bold text-base text-text-primary">Configurar Tarifa Horaria</h3>
          <button id="btn-close-rate-modal" class="text-text-secondary hover:text-text-primary text-sm font-bold">✕</button>
        </div>

        <form id="form-rate" class="space-y-4">
          <input type="hidden" id="modal-rate-id" value="" />
          <div>
            <label class="block text-xs font-semibold text-text-primary mb-1">Nombre de la Tarifa</label>
            <input type="text" id="modal-rate-name-input" placeholder="Ej: Fiverr, Exterior Directo, Local" class="w-full text-xs border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" required />
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Valor por Hora</label>
              <input type="number" step="any" min="0" id="modal-rate-amount-input" placeholder="15000" class="w-full text-xs font-mono font-bold border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" required />
            </div>
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Moneda</label>
              <select id="modal-rate-currency-input" class="w-full text-xs border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none">
                <option value="ARS" selected>ARS (Pesos Argentinos)</option>
                <option value="USD">USD (Dólares)</option>
              </select>
            </div>
          </div>

          <div>
            <label class="block text-xs font-semibold text-text-primary mb-1">Porcentaje de Comisiones / Retenciones (%)</label>
            <input type="number" step="0.01" min="0" max="100" id="modal-rate-comm-input" placeholder="0.00" class="w-full text-xs font-mono border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" />
            <span class="text-[10px] text-text-secondary mt-1 block">Ej: 23.95 para Fiverr, 4.94 para transferencias del exterior, 0 para directo.</span>
          </div>

          <div>
            <label class="block text-xs font-semibold text-text-primary mb-1">Notas / Criterio de Aplicación</label>
            <textarea id="modal-rate-notes-input" rows="2" placeholder="Detalle sobre cuándo usar esta tarifa..." class="w-full text-xs border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none"></textarea>
          </div>

          <div class="pt-3 border-t border-border flex justify-end gap-2">
            <button type="button" id="btn-cancel-rate" class="px-4 py-2 text-xs font-semibold rounded-md border border-border hover:bg-surface-subtle text-text-secondary">
              Cancelar
            </button>
            <button type="submit" class="px-4 py-2 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary-hover active:bg-primary-active">
              Guardar Tarifa
            </button>
          </div>
        </form>
      </div>
    </div>
  `;
}

export function initRatesEvents() {
  const modal = document.getElementById('modal-rate');
  const modalTitle = document.getElementById('modal-rate-title');
  const btnOpen = document.getElementById('btn-open-rate-modal');
  const btnClose = document.getElementById('btn-close-rate-modal');
  const btnCancel = document.getElementById('btn-cancel-rate');

  const openModal = (rate = null) => {
    if (!modal) return;
    modal.classList.remove('hidden');
    document.body.classList.add('overflow-hidden');
    if (rate) {
      modalTitle.textContent = 'Editar Tarifa';
      document.getElementById('modal-rate-id').value = rate.id;
      document.getElementById('modal-rate-name-input').value = rate.name;
      document.getElementById('modal-rate-amount-input').value = rate.amount;
      document.getElementById('modal-rate-currency-input').value = rate.currency;
      document.getElementById('modal-rate-comm-input').value = rate.commission;
      document.getElementById('modal-rate-notes-input').value = rate.notes;
    } else {
      modalTitle.textContent = 'Crear Nueva Tarifa';
      document.getElementById('modal-rate-id').value = '';
      document.getElementById('modal-rate-name-input').value = '';
      document.getElementById('modal-rate-amount-input').value = '';
      document.getElementById('modal-rate-currency-input').value = 'ARS';
      document.getElementById('modal-rate-comm-input').value = '0';
      document.getElementById('modal-rate-notes-input').value = '';
    }
  };

  const closeModal = () => {
    if (!modal) return;
    modal.classList.add('hidden');
    document.body.classList.remove('overflow-hidden');
  };

  if (btnOpen) btnOpen.onclick = () => openModal();
  [btnClose, btnCancel].forEach(b => { if (b) b.onclick = () => closeModal(); });

  document.querySelectorAll('.btn-edit-rate').forEach(btn => {
    btn.onclick = () => {
      openModal({
        id: btn.dataset.rateId,
        name: btn.dataset.rateName,
        amount: btn.dataset.rateAmount,
        currency: btn.dataset.rateCurrency,
        commission: btn.dataset.rateCommission,
        notes: btn.dataset.rateNotes
      });
    };
  });

  const form = document.getElementById('form-rate');
  if (form) {
    form.onsubmit = async (e) => {
      e.preventDefault();
      const id = document.getElementById('modal-rate-id').value;
      const rateData = {
        name: document.getElementById('modal-rate-name-input').value,
        hourly_rate: parseFloat(document.getElementById('modal-rate-amount-input').value) || 0,
        currency: document.getElementById('modal-rate-currency-input').value,
        commission_percent: parseFloat(document.getElementById('modal-rate-comm-input').value) || 0,
        notes: document.getElementById('modal-rate-notes-input').value,
        is_active: true
      };

      try {
        if (id) {
          await DataService.updateRate(id, rateData);
          showToast('Tarifa actualizada');
        } else {
          await DataService.createRate(rateData);
          showToast('Tarifa creada');
        }
        modal.classList.add('hidden');
        document.body.classList.remove('overflow-hidden');
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      } catch (err) {
        showToast(`Error: ${err.message}`, 'error');
      }
    };
  }
}
